// sentryConfig.ts
import * as Sentry from '@sentry/react';
import React from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';

// Refusals core answers on purpose (permission, missing record, validation,
// conflict, rate limit). The user already got a toast; as Sentry issues they
// were pure noise (prod 10-19: a new issue per order for every 403/404).
const EXPECTED_REFUSALS = new Set([401, 403, 404, 409, 422, 429]);

export const isExpectedRefusal = (status: unknown): boolean =>
  typeof status === 'number' && EXPECTED_REFUSALS.has(status);

// One issue per endpoint, not per order: strip the query, ULID/numeric ids and API keys.
export const normalizeApiUrl = (url: string): string =>
  url
    .split('?')[0]
    .replace(/\/api-tokens\/[^/]+/g, '/api-tokens/{key}') // the live API key rides in this path
    .replace(/[0-9A-HJKMNP-TV-Z]{26}/g, '{uid}')
    .replace(/\/\d+(?=\/|$)/g, '/{id}');

// The magic-login link carries a live Sanctum token in its path (/login-with-magic-token/{id}|{secret});
// Sentry records page URLs on events, breadcrumbs and transactions, so scrub it everywhere.
const MAGIC_TOKEN_PATH = /(login-with-magic-token\/)[^/?#"\s\\]+/g;

// Walks plain objects/arrays in place and rewrites strings only. Events and transactions can hold
// DOM nodes and React fibers (circular), so JSON round-tripping threw and dropped the event.
export const scrubMagicToken = <T>(value: T, seen: WeakSet<object> = new WeakSet()): T => {
  if (typeof value === 'string') return value.replace(MAGIC_TOKEN_PATH, '$1{token}') as T;
  if (value === null || typeof value !== 'object' || seen.has(value)) return value;
  const proto = Object.getPrototypeOf(value);
  if (!Array.isArray(value) && proto !== Object.prototype && proto !== null) return value;
  seen.add(value);
  const record = value as Record<string, unknown>;
  Object.keys(record).forEach((key) => {
    record[key] = scrubMagicToken(record[key], seen);
  });
  return value;
};

const getEnvironment = (): string => {
  // Use build-time environment variable first (most reliable)
  if (import.meta.env.VITE_SENTRY_ENVIRONMENT) {
    return import.meta.env.VITE_SENTRY_ENVIRONMENT;
  }

  // Fallback to hostname detection for local development
  if (window.location.hostname.includes('localhost')) {
    return 'local';
  } else if (window.location.hostname.includes('dashboard-stg')) {
    return 'stg';
  } else if (window.location.hostname.includes('dashboard')) {
    return 'prod';
  }
  return 'unknown';
};

const getRelease = (): string | undefined => {
  // Use build-time release variable (commit SHA from CI)
  if (import.meta.env.VITE_SENTRY_RELEASE) {
    return import.meta.env.VITE_SENTRY_RELEASE;
  }

  return undefined;
};

export const initSentry = () => {
  const environment = getEnvironment();
  const release = getRelease();

  // Only initialize Sentry if the environment is not 'local'
  if (environment !== 'local') {
    Sentry.init({
      dsn: 'https://9bbf72a181213ef81f744b71250eef92@sentry.tryboxy.dev/3',
      environment,
      release,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.browserProfilingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: false,
          beforeAddRecordingEvent: (event) => scrubMagicToken(event),
        }),
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],
      // 10-15: Sentry was overloaded; replays only on errors, traces a 1% sample.
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: environment === 'prod' ? 0.5 : 0.2,
      tracesSampleRate: 0.01,
      profilesSampleRate: 0,
      tracePropagationTargets: ['localhost', 'dashboard-stg.tryboxy.dev', 'dashboard.tryboxy.com'],

      // Customize error fingerprinting for better grouping
      beforeSend(event, hint) {
        const error = hint.originalException;

        // Handle Axios errors with better grouping
        if (error && typeof error === 'object' && 'isAxiosError' in error) {
          const axiosError = error as any;
          if (isExpectedRefusal(axiosError.response?.status)) return null;

          const url = normalizeApiUrl(axiosError.config?.url || 'unknown');
          const method = axiosError.config?.method?.toUpperCase() || 'UNKNOWN';
          const status = axiosError.response?.status || 'network';

          // Group by endpoint + status code instead of generic AxiosError
          event.fingerprint = [`axios-${method}-${url}-${status}`];

          // Add custom tags for filtering
          event.tags = {
            ...event.tags,
            api_endpoint: url,
            http_method: method,
            http_status: status.toString(),
            error_type:
              status >= 500 ? 'server_error' : status >= 400 ? 'client_error' : 'network_error',
          };

          // Improve error message
          const errorMessage = axiosError.response?.data?.message || axiosError.message;
          event.message = `API Error: ${method} ${url} - ${status} ${errorMessage}`;
        }

        // Sanitize sensitive data
        if (event.request?.headers) {
          delete event.request.headers['Authorization'];
          delete event.request.headers['authorization'];
        }

        return scrubMagicToken(event);
      },

      beforeSendTransaction(event) {
        return scrubMagicToken(event);
      },

      // Filter breadcrumbs to reduce noise
      beforeBreadcrumb(breadcrumb) {
        // Skip console breadcrumbs in production
        if (environment === 'prod' && breadcrumb.category === 'console') {
          return null;
        }
        return scrubMagicToken(breadcrumb);
      },
    });
  }
};
